import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, RefreshCcw, Filter, CheckCircle2, Clock, XCircle, Undo2, X, ExternalLink, DownloadCloud } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Input } from '../../../components/ui/input';
import PaymentRevenueWidget from '../../../components/dashboard/PaymentRevenueWidget';
import PaymentForecastWidget from '../../../components/dashboard/PaymentForecastWidget';
import {
  paymentsApi,
  formatPaymentMethod,
  isAsyncPaymentMethod,
  type PaymentAnomaly,
  type PaymentAnomalyStatus,
  type PaymentTransaction,
  type PaymentTransactionStatus,
  type PaymentSummary,
  type TransactionDetail,
} from '../../../lib/api/payments';
import { useStandort } from '../../../components/layouts/use-standort';

const STATUS_LABEL: Record<string, string> = {
  succeeded: 'Erfolgreich',
  processing: 'Verarbeitung',
  requires_action: 'Wartet auf Aktion',
  requires_capture: 'Wartet auf Capture',
  requires_payment_method: 'Wartet auf Zahlungsmittel',
  requires_confirmation: 'Wartet auf Bestätigung',
  canceled: 'Storniert',
  failed: 'Fehlgeschlagen',
  refunded: 'Erstattet',
  dispute_open: 'Widerspruch offen',
  dispute_closed: 'Widerspruch geschlossen',
  mandate_revoked: 'SEPA-Mandat widerrufen',
  payment_method_detached: 'Zahlungsmittel entfernt',
  setup_succeeded: 'Setup erfolgreich',
  setup_failed: 'Setup fehlgeschlagen',
};

const STATUS_VARIANT: Record<string, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  succeeded: 'default',
  processing: 'secondary',
  requires_action: 'secondary',
  requires_capture: 'secondary',
  requires_payment_method: 'outline',
  requires_confirmation: 'outline',
  canceled: 'secondary',
  failed: 'destructive',
  refunded: 'secondary',
  dispute_open: 'destructive',
  dispute_closed: 'outline',
  mandate_revoked: 'destructive',
  payment_method_detached: 'destructive',
  setup_succeeded: 'outline',
  setup_failed: 'destructive',
};

const ALL_STATUSES: PaymentTransactionStatus[] = [
  'succeeded',
  'processing',
  'requires_action',
  'failed',
  'canceled',
  'refunded',
  'dispute_open',
  'dispute_closed',
  'mandate_revoked',
  'payment_method_detached',
];

const ANOMALY_DEFAULT: PaymentAnomalyStatus[] = [
  'failed',
  'dispute_open',
  'refunded',
  'mandate_revoked',
  'payment_method_detached',
];

function formatDate(iso: string | null): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('de-DE');
}

function formatAmount(cents: number | null, currency?: string | null): string {
  if (cents === null || cents === undefined) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: (currency || 'EUR').toUpperCase(),
  }).format(cents / 100);
}

export default function PaymentsOverview() {
  const { selectedStandortId } = useStandort();
  const [tab, setTab] = useState<'all' | 'anomalies'>('all');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [syncNonce, setSyncNonce] = useState(0);

  const handleSync = async (sinceDays: number) => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const since = new Date();
      since.setDate(since.getDate() - sinceDays);
      const res = await paymentsApi.syncFromStripe({
        since: String(Math.floor(since.getTime() / 1000)),
      });
      setSyncResult(`${res.synced} Transaktion(en) synchronisiert${res.failed > 0 ? `, ${res.failed} Fehler` : ''}.`);
      setSyncNonce((n) => n + 1); // triggert Reload der Kinder
    } catch (e) {
      setSyncResult(e instanceof Error ? `Sync fehlgeschlagen: ${e.message}` : 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Zahlungen</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Alle Stripe-Transaktionen, Abos, Nachbuchungen, Raten und Anomalien
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button variant="outline" size="sm" onClick={() => void handleSync(30)} disabled={syncing}>
            <DownloadCloud className={`mr-2 h-4 w-4 ${syncing ? 'animate-pulse' : ''}`} />
            {syncing ? 'Synchronisiere…' : 'Aus Stripe synchronisieren'}
          </Button>
          {syncResult && <span className="text-xs text-muted-foreground">{syncResult}</span>}
        </div>
      </div>

      <SummaryCards standortId={selectedStandortId ?? undefined} reloadKey={syncNonce} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PaymentRevenueWidget standortId={selectedStandortId ?? undefined} daysWindow={30} />
        <PaymentForecastWidget standortId={selectedStandortId ?? undefined} daysAhead={30} />
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'all' | 'anomalies')}>
        <TabsList>
          <TabsTrigger value="all">Alle Transaktionen</TabsTrigger>
          <TabsTrigger value="anomalies">Anomalien</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <AllTransactionsTab standortId={selectedStandortId ?? undefined} reloadKey={syncNonce} onAutoSync={handleSync} />
        </TabsContent>

        <TabsContent value="anomalies" className="mt-4">
          <AnomaliesTab standortId={selectedStandortId ?? undefined} reloadKey={syncNonce} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ──────────────────── SUMMARY CARDS ────────────────────

function SummaryCards({ standortId, reloadKey = 0 }: { standortId?: number; reloadKey?: number }) {
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const since = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  }, [days]);

  useEffect(() => {
    setLoading(true);
    paymentsApi
      .summary({ standortId, since })
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [standortId, since, reloadKey]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Zeitraum:</span>
        {[7, 30, 90, 365].map((d) => (
          <Button
            key={d}
            type="button"
            size="sm"
            variant={days === d ? 'default' : 'outline'}
            onClick={() => setDays(d)}
          >
            {d === 365 ? '1 Jahr' : `${d} Tage`}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
          label="Erfolgreich"
          value={loading ? '…' : String(summary?.succeededCount ?? 0)}
          sub={loading ? '' : formatAmount(summary?.succeededVolumeCents ?? 0, 'EUR')}
        />
        <KpiCard
          icon={<Clock className="h-4 w-4 text-amber-500" />}
          label="In Verarbeitung"
          value={loading ? '…' : String(summary?.processingCount ?? 0)}
          sub="SEPA / 3DS offen"
        />
        <KpiCard
          icon={<XCircle className="h-4 w-4 text-red-500" />}
          label="Fehlgeschlagen"
          value={loading ? '…' : String(summary?.failedCount ?? 0)}
          sub={loading ? '' : formatAmount(summary?.failedVolumeCents ?? 0, 'EUR')}
        />
        <KpiCard
          icon={<Undo2 className="h-4 w-4 text-blue-500" />}
          label="Erstattungen"
          value={loading ? '…' : String(summary?.refundedCount ?? 0)}
          sub={loading ? '' : formatAmount(summary?.refundedVolumeCents ?? 0, 'EUR')}
        />
      </div>

      {!loading && ((summary?.succeededFeeCents ?? 0) > 0 || (summary?.succeededNetCents ?? 0) > 0) && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
          <KpiCard
            icon={<Undo2 className="h-4 w-4 text-slate-500" />}
            label="Stripe-Gebühren (erfolgreich)"
            value={formatAmount(summary?.succeededFeeCents ?? 0, 'EUR')}
            sub={
              (summary?.succeededVolumeCents ?? 0) > 0
                ? `${(((summary?.succeededFeeCents ?? 0) / (summary?.succeededVolumeCents ?? 1)) * 100).toFixed(2)} % vom Umsatz`
                : ''
            }
          />
          <KpiCard
            icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            label="Netto-Umsatz (nach Gebühren)"
            value={formatAmount(summary?.succeededNetCents ?? 0, 'EUR')}
            sub="nur erfolgreiche Zahlungen"
          />
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
        <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

// ──────────────────── ALLE TRANSAKTIONEN ────────────────────

function AllTransactionsTab({
  standortId,
  reloadKey = 0,
  onAutoSync,
}: {
  standortId?: number;
  reloadKey?: number;
  onAutoSync?: (days: number) => Promise<void>;
}) {
  const [rows, setRows] = useState<PaymentTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PaymentTransactionStatus[]>([]);
  const [sinceDays, setSinceDays] = useState(30);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const pageSize = 100;
  const autoSyncTried = useRef(false);
  // Erst nach dem ersten abgeschlossenen Laden weiß man, ob die Liste leer ist.
  // Vorher stehen `loading` und `total` noch auf ihren Startwerten.
  const [ersteLadungFertig, setErsteLadungFertig] = useState(false);

  const sinceIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - sinceDays);
    return d.toISOString().slice(0, 10);
  }, [sinceDays]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await paymentsApi.listTransactions({
        since: sinceIso,
        status: statusFilter.length > 0 ? statusFilter : undefined,
        standortId,
        search: debouncedSearch || undefined,
        limit: pageSize,
        offset,
      });
      setRows(Array.isArray(res?.rows) ? res.rows : []);
      setTotal(typeof res?.total === 'number' ? res.total : 0);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setLoading(false);
      setErsteLadungFertig(true);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinceIso, statusFilter.join(','), standortId, debouncedSearch, offset, reloadKey]);

  useEffect(() => {
    setOffset(0);
  }, [sinceIso, statusFilter.join(','), standortId, debouncedSearch]);

  // Auto-Sync beim ersten Laden, wenn die lokale Tabelle leer ist und keine Filter aktiv sind
  useEffect(() => {
    if (autoSyncTried.current) return;
    if (!ersteLadungFertig || loading) return;
    if (total > 0) return;
    if (statusFilter.length > 0 || debouncedSearch) return;
    autoSyncTried.current = true;
    if (onAutoSync) {
      void onAutoSync(sinceDays);
    }
  }, [ersteLadungFertig, total, loading, statusFilter.length, debouncedSearch, sinceDays, onAutoSync]);

  const toggleStatus = (s: PaymentTransactionStatus) => {
    setStatusFilter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Zeitraum:</span>
            {[7, 30, 90, 365].map((d) => (
              <Button
                key={d}
                type="button"
                size="sm"
                variant={sinceDays === d ? 'default' : 'outline'}
                onClick={() => setSinceDays(d)}
              >
                {d === 365 ? '1 Jahr' : `${d} Tage`}
              </Button>
            ))}
          </div>
          <div>
            <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Status (leer = alle)</p>
            <div className="flex flex-wrap gap-2">
              {ALL_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs transition ${
                    statusFilter.includes(s)
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Input
              placeholder="Suche: PaymentIntent-ID, Kundenname oder Enrollment-ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Transaktionen <span className="font-normal text-muted-foreground">({total})</span>
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
          {!loading && rows.length === 0 && !error && (
            <p className="py-8 text-center text-sm text-muted-foreground">Keine Transaktionen in diesem Zeitraum.</p>
          )}
          {rows.length > 0 && <TransactionsTable rows={rows} onOpen={setSelected} />}
          {total > pageSize && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {offset + 1}–{Math.min(offset + pageSize, total)} von {total}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>
                  Zurück
                </Button>
                <Button size="sm" variant="outline" disabled={offset + pageSize >= total} onClick={() => setOffset(offset + pageSize)}>
                  Weiter
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && <TransactionDrawer paymentIntentId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// Je Transaktion der Standort (BUCH-14.1) — auch wenn oben „Alle Standorte“ gewählt ist.
function useStandortName() {
  const { standorte } = useStandort();
  return (id: number | null | undefined) =>
    id == null ? '–' : (standorte.find((s) => s.id === id)?.name ?? `Standort #${id}`);
}

function TransactionsTable({ rows, onOpen }: { rows: PaymentTransaction[]; onOpen: (id: string) => void }) {
  const standortName = useStandortName();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Status</th>
            <th className="py-2 pr-3 font-medium">Zahlungsmethode</th>
            <th className="py-2 pr-3 font-medium">Typ</th>
            <th className="py-2 pr-3 font-medium">Kunde</th>
            <th className="py-2 pr-3 font-medium">Standort</th>
            <th className="py-2 pr-3 font-medium text-right">Betrag</th>
            <th className="py-2 pr-3 font-medium">Datum</th>
            <th className="py-2 pr-3 font-medium">Anmeldung</th>
            <th className="py-2 font-medium">PaymentIntent</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const wpIdFromSub = r.subprofile_id?.split('-')[0];
            const purchaseType = r.package_name
              ? (r.portal_purchase_status ? 'Nachbuchung' : 'Initial')
              : (r.payment_type === 'installment' ? `Rate` : 'Erstbelastung');
            const methodLabel = formatPaymentMethod(r);
            const isAsync = isAsyncPaymentMethod(r.payment_method_type);
            // Bei SEPA o.ä. in Verarbeitung: deutlich als „eingezogen, wartet auf Bestätigung" markieren
            const processingHint = r.current_status === 'processing' && isAsync
              ? 'Eingezogen, wartet auf Bankbestätigung (ca. 5–7 Werktage)'
              : null;
            return (
              <tr
                key={r.payment_intent_id}
                className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
                onClick={() => onOpen(r.payment_intent_id)}
              >
                <td className="py-3 pr-3">
                  <Badge variant={STATUS_VARIANT[r.current_status] ?? 'outline'}>
                    {STATUS_LABEL[r.current_status] ?? r.current_status}
                  </Badge>
                  {processingHint && (
                    <div className="mt-1 text-xs text-muted-foreground">{processingHint}</div>
                  )}
                </td>
                <td className="py-3 pr-3 text-xs">{methodLabel}</td>
                <td className="py-3 pr-3 text-xs">
                  <span className="text-muted-foreground">{purchaseType}</span>
                  {r.package_name && (
                    <span className="ml-1 text-foreground">· {r.package_name}</span>
                  )}
                </td>
                <td className="py-3 pr-3">
                  {wpIdFromSub ? (
                    <Link
                      to={`/dashboard/people/${wpIdFromSub}?tab=payments`}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:underline"
                    >
                      {r.student_name || `User #${wpIdFromSub}`}
                    </Link>
                  ) : (
                    r.student_name || '–'
                  )}
                </td>
                <td className="py-3 pr-3 text-xs">{standortName(r.standort_id)}</td>
                <td className="py-3 pr-3 text-right font-medium tabular-nums">
                  {formatAmount(r.amount_cents, r.currency)}
                </td>
                <td className="py-3 pr-3 text-xs text-muted-foreground">
                  {formatDate(r.last_event_created_at)}
                </td>
                <td className="py-3 pr-3 text-xs">{r.enrollment_id ? `#${r.enrollment_id}` : '–'}</td>
                <td className="py-3 font-mono text-xs text-muted-foreground" title={r.payment_intent_id}>
                  {r.payment_intent_id.slice(0, 18)}…
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ──────────────────── DETAIL DRAWER ────────────────────

function TransactionDrawer({ paymentIntentId, onClose }: { paymentIntentId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<TransactionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    paymentsApi
      .transactionDetail(paymentIntentId)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }, [paymentIntentId]);

  const stripeLink = `https://dashboard.stripe.com/payments/${paymentIntentId}`;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40 backdrop-blur-sm" />
      <div
        className="flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b p-4">
          <div>
            <h2 className="font-semibold">Transaktion</h2>
            <p className="text-xs text-muted-foreground font-mono">{paymentIntentId}</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={stripeLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              Stripe <ExternalLink className="h-3 w-3" />
            </a>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Schließen">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 space-y-4 p-4">
          {loading && <p className="text-sm text-muted-foreground">Lade…</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {detail && <TransactionDetailBody detail={detail} />}
        </div>
      </div>
    </div>
  );
}

function TransactionDetailBody({ detail }: { detail: TransactionDetail }) {
  const tx = detail.transaction;
  const metadata = tx.metadata_json ? (() => {
    try { return JSON.parse(tx.metadata_json as string); } catch { return null; }
  })() : null;
  const wpIdFromSub = tx.subprofile_id?.split('-')[0];
  const standortName = useStandortName();

  return (
    <>
      <Card>
        <CardContent className="space-y-2 p-4 text-sm">
          <div className="flex items-center justify-between">
            <Badge variant={STATUS_VARIANT[tx.current_status] ?? 'outline'}>
              {STATUS_LABEL[tx.current_status] ?? tx.current_status}
            </Badge>
            <span className="font-bold tabular-nums">{formatAmount(tx.amount_cents, tx.currency)}</span>
          </div>
          {tx.last_error && <p className="text-xs text-destructive">{tx.last_error}</p>}
          {(tx.stripe_fee_cents !== null && tx.stripe_fee_cents !== undefined) && (
            <div className="flex items-center justify-between rounded-md bg-muted/50 px-2 py-1 text-xs tabular-nums">
              <span className="text-muted-foreground">Stripe-Gebühr</span>
              <span>
                {formatAmount(tx.stripe_fee_cents, tx.stripe_fee_currency ?? tx.currency)}
                {tx.amount_cents && tx.amount_cents > 0 && tx.stripe_fee_cents > 0
                  ? ` · ${((tx.stripe_fee_cents / tx.amount_cents) * 100).toFixed(2)} %`
                  : ''}
              </span>
            </div>
          )}
          {(tx.net_amount_cents !== null && tx.net_amount_cents !== undefined) && (
            <div className="flex items-center justify-between rounded-md bg-muted/50 px-2 py-1 text-xs tabular-nums">
              <span className="text-muted-foreground">Netto (Auszahlung)</span>
              <span>{formatAmount(tx.net_amount_cents, tx.stripe_fee_currency ?? tx.currency)}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div>Erstellt: {formatDate(tx.created_at)}</div>
            <div>Letztes Event: {formatDate(tx.last_event_created_at)}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Kontext</CardTitle></CardHeader>
        <CardContent className="space-y-1 p-4 text-sm">
          <Row label="Standort">{standortName(tx.standort_id)}</Row>
          <Row label="Zahlungsmethode">{formatPaymentMethod(tx)}</Row>
          {tx.student_name && (
            <Row label="Kunde">
              {wpIdFromSub ? (
                <Link to={`/dashboard/people/${wpIdFromSub}?tab=payments`} className="hover:underline">
                  {tx.student_name}
                </Link>
              ) : tx.student_name}
            </Row>
          )}
          {tx.subprofile_id && <Row label="Schülerprofil">{tx.subprofile_id}</Row>}
          {tx.enrollment_id && <Row label="Anmeldung">#{tx.enrollment_id}</Row>}
          {tx.payment_type && <Row label="Zahlungsart">{tx.payment_type === 'installment' ? `Ratenzahlung (${tx.installments || '?'})` : 'Sofortzahlung'}</Row>}
          {tx.package_name && <Row label="Paket">{tx.package_name} {tx.package_units ? `(${tx.package_units} Einheiten)` : ''}</Row>}
          {tx.portal_purchase_status && <Row label="Kauf-Status">{tx.portal_purchase_status}{tx.fulfilled_at ? ` · erfüllt am ${formatDate(tx.fulfilled_at)}` : ''}</Row>}
          {tx.failure_reason && <Row label="Fehlerursache"><span className="text-destructive">{tx.failure_reason}</span></Row>}
          {isAsyncPaymentMethod(tx.payment_method_type) && tx.current_status === 'processing' && (
            <Row label="SEPA-Status"><span className="text-amber-600">Eingezogen, wartet auf Bankbestätigung (5–7 Werktage üblich)</span></Row>
          )}
        </CardContent>
      </Card>

      {detail.installment && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Rate</CardTitle></CardHeader>
          <CardContent className="space-y-1 p-4 text-sm">
            <Row label="Rate-Nr">{detail.installment.sequence_no}</Row>
            <Row label="Fällig">{formatDate(detail.installment.due_at)}</Row>
            <Row label="Geplant">{formatAmount(detail.installment.planned_amount_cents, tx.currency)}</Row>
            <Row label="Belastet">{formatAmount(detail.installment.actual_charged_cents, tx.currency)}</Row>
            {detail.installment.coupon_applied_cents > 0 && (
              <Row label="Coupon-Rabatt">{formatAmount(detail.installment.coupon_applied_cents, tx.currency)}</Row>
            )}
            <Row label="Retry-Versuche">{detail.installment.retry_count}</Row>
            {detail.installment.last_error && <Row label="Letzter Fehler"><span className="text-destructive">{detail.installment.last_error}</span></Row>}
          </CardContent>
        </Card>
      )}

      {detail.ledgerEntries.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Kontingent-Buchungen</CardTitle></CardHeader>
          <CardContent className="space-y-2 p-4 text-sm">
            {detail.ledgerEntries.map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded border p-2 text-xs">
                <div>
                  <div className="font-medium">{e.entry_type} · {e.source_type}</div>
                  <div className="text-muted-foreground">{formatDate(e.created_at)}</div>
                </div>
                <div className="text-right">
                  <div className={e.units < 0 ? 'text-red-500' : 'text-emerald-500'}>
                    {e.units > 0 ? '+' : ''}{e.units} Einheiten
                  </div>
                  {e.purchase_total_cents && <div className="text-muted-foreground">{formatAmount(e.purchase_total_cents, e.currency)}</div>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {metadata && Object.keys(metadata).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Stripe-Metadata</CardTitle></CardHeader>
          <CardContent className="p-4">
            <pre className="overflow-x-auto rounded bg-muted/50 p-2 text-xs">{JSON.stringify(metadata, null, 2)}</pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-sm">Webhook-Events ({detail.webhookEvents.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2 p-4 text-sm">
          {detail.webhookEvents.length === 0 && <p className="text-xs text-muted-foreground">Keine Events gefunden.</p>}
          {detail.webhookEvents.map((e) => (
            <div key={e.id} className="rounded border p-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-mono">{e.event_type}</span>
                <span className="text-muted-foreground">{formatDate(e.event_created_at)}</span>
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs">
                {e.is_processed === 1 ? (
                  <span className="text-emerald-500">✓ verarbeitet</span>
                ) : (
                  <span className="text-amber-500">⧗ offen</span>
                )}
                {!e.livemode && <span className="text-muted-foreground">(test)</span>}
              </div>
              {e.processing_error && <p className="mt-1 text-destructive">{e.processing_error}</p>}
            </div>
          ))}
        </CardContent>
      </Card>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b last:border-b-0 py-1.5">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

// ──────────────────── ANOMALIEN (existierend) ────────────────────

function AnomaliesTab({ standortId, reloadKey = 0 }: { standortId?: number; reloadKey?: number }) {
  const [rows, setRows] = useState<PaymentAnomaly[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PaymentAnomalyStatus[]>(ANOMALY_DEFAULT);
  const [sinceDays, setSinceDays] = useState(30);

  const sinceIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - sinceDays);
    return d.toISOString().slice(0, 10);
  }, [sinceDays]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await paymentsApi.listAnomalies({
        since: sinceIso,
        status: statusFilter,
        standortId,
        limit: 500,
      });
      setRows(Array.isArray(res?.rows) ? res.rows : []);
    } catch (e) {
      setRows([]);
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinceIso, statusFilter.join(','), standortId, reloadKey]);

  const toggleStatus = (s: PaymentAnomalyStatus) => {
    setStatusFilter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Zeitraum:</span>
            {[7, 30, 90, 365].map((d) => (
              <Button
                key={d}
                type="button"
                size="sm"
                variant={sinceDays === d ? 'default' : 'outline'}
                onClick={() => setSinceDays(d)}
              >
                {d === 365 ? '1 Jahr' : `${d} Tage`}
              </Button>
            ))}
          </div>
          <div>
            <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Status</p>
            <div className="flex flex-wrap gap-2">
              {(['failed', 'dispute_open', 'dispute_closed', 'refunded', 'canceled', 'mandate_revoked', 'payment_method_detached'] as PaymentAnomalyStatus[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs transition ${
                    statusFilter.includes(s)
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  {STATUS_LABEL[s] ?? s}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Anomalien ({rows.length})
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
          {!loading && rows.length === 0 && !error && (
            <p className="py-8 text-center text-sm text-muted-foreground">Alles ruhig — keine Anomalien.</p>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium">Kunde</th>
                    <th className="py-2 pr-3 font-medium text-right">Betrag</th>
                    <th className="py-2 pr-3 font-medium">Datum</th>
                    <th className="py-2 pr-3 font-medium">Anmeldung</th>
                    <th className="py-2 font-medium">Fehler</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const wpIdFromSub = r.subprofile_id?.split('-')[0];
                    return (
                      <tr key={r.payment_intent_id} className="border-b last:border-b-0">
                        <td className="py-3 pr-3">
                          <Badge variant={STATUS_VARIANT[r.current_status] ?? 'outline'}>
                            {STATUS_LABEL[r.current_status] ?? r.current_status}
                          </Badge>
                        </td>
                        <td className="py-3 pr-3">
                          {wpIdFromSub ? (
                            <Link to={`/dashboard/people/${wpIdFromSub}?tab=payments`} className="hover:underline">
                              {r.student_name || `User #${wpIdFromSub}`}
                            </Link>
                          ) : (
                            r.student_name || '–'
                          )}
                        </td>
                        <td className="py-3 pr-3 text-right font-medium tabular-nums">{formatAmount(r.amount_cents)}</td>
                        <td className="py-3 pr-3 text-xs text-muted-foreground">{formatDate(r.last_event_created_at)}</td>
                        <td className="py-3 pr-3 text-xs">{r.enrollment_id ? `#${r.enrollment_id}` : '–'}</td>
                        <td className="py-3 text-xs text-destructive max-w-[300px] truncate" title={r.last_error ?? ''}>
                          {r.last_error ?? '–'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
